import { ReactNode, HTMLAttributes } from 'react';
import clsx from 'clsx';
import { Button } from './Button';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'ghost';
  icon?: ReactNode;
}

export interface EmptyStateProps extends HTMLAttributes<HTMLDivElement> {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  size?: 'sm' | 'md' | 'lg';
}

const sizeStyles: Record<NonNullable<EmptyStateProps['size']>, string> = {
  sm: 'py-8',
  md: 'py-12',
  lg: 'py-16',
};

export const EmptyState = ({
  icon,
  title,
  description,
  action,
  secondaryAction,
  size = 'md',
  className,
  ...props
}: EmptyStateProps) => {
  return (
    <div
      className={clsx(
        'flex flex-col items-center justify-center text-center',
        sizeStyles[size],
        className
      )}
      {...props}
    >
      {icon && (
        <div style={{ color: 'var(--color-text-muted)' }}>
          {icon}
        </div>
      )}

      <h3 className="mt-3 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
        {title}
      </h3>

      {description && (
        <p className="mt-1 text-sm max-w-sm" style={{ color: 'var(--color-text-secondary)' }}>
          {description}
        </p>
      )}

      {action && (
        <div className="mt-6">
          <Button
            variant={action.variant ?? 'primary'}
            onClick={action.onClick}
            leftIcon={action.icon}
          >
            {action.label}
          </Button>
        </div>
      )}

      {secondaryAction && (
        <div className="mt-2">
          <Button
            variant={secondaryAction.variant ?? 'ghost'}
            onClick={secondaryAction.onClick}
            leftIcon={secondaryAction.icon}
          >
            {secondaryAction.label}
          </Button>
        </div>
      )}
    </div>
  );
};
